
import React from "react";
import {
  FileText,
  Settings,
  Folder,
  CheckCircle,
  MapPin,
  Calendar,
  Shield,
  Upload,
  Tag,
  Eye,
  Layers,
  BadgeDollarSign,
  BadgeAlert,
  Blinds,
  LucideTextCursor,
  LayoutListIcon,
  Library,
  Grid2X2Icon,
  Group,
  AlignVerticalSpaceAround,
} from "lucide-react";
import type { Field, DocumentItem } from "../types/rfq";

export const CERTIFICATIONS = [
  "ISO9001",
  "IATF16949",
  "ISO14001",
  "ISO45001",
  "ISO27001",
];

export const DOCUMENT_TYPE_OPTIONS = [
  { value: "plan", label: "Plan technique" },
  { value: "cdc", label: "Cahier des charges" },
  { value: "spec", label: "Spécifications" },
  { value: "3d", label: "Modèle 3D" },
  { value: "photo", label: "Photo" },
  { value: "autre", label: "Autre" },
];

export const NON_NEGATIVE_FIELDS = ["quantity", "annual_volume", "budget"];
export const COMPACT_CHOICE_FIELDS = ["request_type", "priority", "criteria"];

export const initialState = {
  documents: [] as DocumentItem[],
  cities: [] as string[],
};

export const stepDescriptions: Record<number, string> = {
  1: "Décrivez clairement votre besoin industriel",
  2: "Précisez les exigences techniques et logistiques",
  3: "Ajoutez les documents techniques nécessaires",
  4: "Dernière étape avant l’envoi de la demande",
};

export const steps = [
  { id: 1, title: "Informations", icon: <FileText size={16} /> },
  { id: 2, title: "Spécifications", icon: <Settings size={16} /> },
  { id: 3, title: "Documents", icon: <Folder size={16} /> },
  { id: 4, title: "Publication", icon: <CheckCircle size={16} /> },
];

const iconStyle = {
  size: 16,
  color: "#f97316",
};

export const getFieldIcon = (id: string) => {
  switch (id) {
    case "title":
      return <LucideTextCursor {...iconStyle} />;
    case "category":
      return <Folder {...iconStyle} />;
    case "segment":
      return <Grid2X2Icon {...iconStyle} />;
    case "programme":
      return <Library {...iconStyle} />;
    case "reference_piece":
      return <Group {...iconStyle} />;
    case "sourcing_type":
      return <AlignVerticalSpaceAround {...iconStyle} />;
    case "description":
      return <FileText {...iconStyle} />;
    case "request_type":
      return <Settings {...iconStyle} />;
    case "priority":
      return <LayoutListIcon {...iconStyle} />;
    case "confidentiality":
      return <Shield {...iconStyle} />;
    case "quantity":
      return <Blinds {...iconStyle} />;
    case "annual_volume":
      return <Layers {...iconStyle} />;
    case "start_date":
    case "deadline":
    case "delivery_date":
    case "response_deadline":
      return <Calendar {...iconStyle} />;
    case "certifications":
      return <Shield {...iconStyle} />;
    case "country":
    case "cities":
      return <MapPin {...iconStyle} />;
    case "logistics":
      return <Settings {...iconStyle} />;
    case "incoterm":
      return <Tag {...iconStyle} />;
    case "budget_type":
      return <BadgeAlert {...iconStyle} />;
    case "budget":
      return <BadgeDollarSign {...iconStyle} />;
    case "documents":
      return <Upload {...iconStyle} />;
    case "visibility":
      return <Eye {...iconStyle} />;
    case "criteria":
      return <CheckCircle {...iconStyle} />;
    case "message":
      return <FileText {...iconStyle} />;
    default:
      return <Settings {...iconStyle} />;
  }
};

export const step1: Field[] = [
  {
    id: "title",
    label: "Titre de la demande",
    type: "text",
    col: 6,
    placeholder: "Ex: Fabrication de pièces plastiques injectées",
  },
  {
    id: "category",
    label: "Catégorie",
    type: "SelectGroup",
    col: 6,
    searchable: false,
    options: [
      { value: "mecanique", label: "Pièces mécaniques" },
      { value: "plastique", label: "Pièces plastiques" },
      { value: "electronique", label: "Électronique" },
      { value: "services", label: "Services industriels" },
    ],
  },
  {
    id: "segment",
    label: "Segment",
    type: "text",
    col: 6,
    placeholder: "Ex: Automobile",
  },
  {
    id: "programme",
    label: "Programme",
    type: "text",
    col: 6,
    placeholder: "Ex: Programme 2026",
  },
  {
    id: "reference_piece",
    label: "Référence pièce",
    type: "text",
    col: 6,
    placeholder: "Ex: REF-PL-0021",
  },
  {
    id: "sourcing_type",
    label: "Type de sourcing",
    type: "radio",
    col: 6,
    options: ["Local", "International", "Mixte"],
  },
  {
    id: "request_type",
    label: "Type de demande",
    type: "checkbox",
    col: 12,
    options: [
      "Prototype",
      "Série pilote",
      "Production série",
      "Prestation de service",
    ],
  },
  {
    id: "priority",
    label: "Priorité",
    type: "radio",
    col: 12,
    options: ["Faible", "Normale", "Urgente"],
  },
  {
    id: "description",
    label: "Description",
    type: "textarea",
    col: 12,
    placeholder:
      "Décrivez précisément votre besoin : produit, matière, quantité, contraintes, dates, exigences...",
  },
];

export const step2: Field[] = [
  { id: "start_date", label: "Date démarrage", type: "date", col: 4 },
  { id: "deadline", label: "Date limite offre", type: "date", col: 4 },
  { id: "delivery_date", label: "1ère livraison", type: "date", col: 4 },
  {
    id: "country",
    label: "Pays fournisseur",
    type: "SelectGroup",
    col: 6,
    searchable: true,
    options: [],
  },
  { id: "cities", label: "Villes", type: "tags", col: 6 },
  {
    id: "quantity",
    label: "Quantité demandée",
    type: "number",
    col: 4,
    placeholder: "Ex: 1000",
  },
  {
    id: "annual_volume",
    label: "Volume annuel",
    type: "number",
    col: 4,
    placeholder: "Ex: 12000",
  },
  {
    id: "budget",
    label: "Budget estimatif",
    type: "number",
    col: 4,
    placeholder: "Ex: 50000",
  },
  {
    id: "incoterm",
    label: "Incoterm",
    type: "text",
    col: 6,
    placeholder: "FOB / EXW / DDP",
  },
  {
    id: "certifications",
    label: "Certifications requises",
    type: "certifications",
    col: 4,
  },
  {
    id: "logistics",
    label: "Exigences logistiques",
    type: "checkbox",
    col: 8,
    options: [
      "Livraison sur site",
      "Conditionnement spécifique",
      "Étiquetage",
      "Traçabilité",
    ],
  },
  {
    id: "budget_type",
    label: "Type budget",
    type: "radio",
    col: 12,
    options: ["À discuter", "Indicatif", "Fixe"],
  },
];

export const step3: Field[] = [
  { id: "documents", label: "Documents techniques", type: "file", col: 12 },
];

export const step4: Field[] = [
  { id: "response_deadline", label: "Date limite réponse", type: "date", col: 6 },
  {
    id: "visibility",
    label: "Visibilité de la demande",
    type: "radio",
    col: 6,
    options: [
      "Tous fournisseurs qualifiés",
      "Fournisseurs présélectionnés",
      "Privé / invitation",
    ],
  },
  {
    id: "criteria",
    label: "Critères d’évaluation",
    type: "checkbox",
    col: 12,
    options: [
      "Prix",
      "Qualité",
      "Délai",
      "Capacité",
      "Localisation",
      "Certification",
      "Expérience",
    ],
  },
  {
    id: "message",
    label: "Message final",
    type: "textarea",
    col: 12,
    placeholder: "Message complémentaire à destination des fournisseurs",
  },
];

export const wizardConfig: Record<number, Field[]> = {
  1: step1,
  2: step2,
  3: step3,
  4: step4,
};
